// import axios from 'axios';


export default {
  namespaced: true,
  
  state: {
    walletBalance: 45000,
    nextPaymentDate: '2023-10-15',
    paymentSchedule: 'Weekly (Every Monday)',
    totalAgentEarnings: 12500,
    
    companies: [
      { id: 'bank-a', name: 'Bank A' },
      { id: 'bank-b', name: 'Bank B' },
      { id: 'bank-c', name: 'Bank C' }
    ],
    
    agentEarnings: [],
    walletTransactions: [],
    paymentSchedules: [],
    wallets: [],
    auditLogs: [],
    
    loading: false,
    error: null,
    currentTab: 'approved',
    companyFilter: 'all',
    periodFilter: 'current',
    searchQuery: '',
    isSuperAdmin: false,
    isCompanyAdmin: false,
    currentCompany: null,
    availableTabs: [
        { id: 'approved', name: 'Approved Payments' },
        { id: 'pending', name: 'Pending Payments' },
        { id: 'rejected', name: 'Rejected Payments' },
        { id: 'refunds', name: 'Refunds' },
    ]
  },
  
  getters: {
    filteredEarnings: (state) => {
      let filtered = [...state.agentEarnings];
      
      // Company filtering for super admin
      if (state.isSuperAdmin && state.companyFilter !== 'all') {
        const companyName = state.companies.find(c => c.id === state.companyFilter)?.name;
        filtered = filtered.filter(e => e.company === companyName);
      }
      
      // For company admin, only show their company's earnings
      if (state.isCompanyAdmin && state.currentCompany) {
        filtered = filtered.filter(e => e.company === state.currentCompany);
      }
      
      // Period filtering
      if (state.periodFilter === 'current') {
        // For demo, assume current period is anything in the last 7 days
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        // This would need actual period logic in a real app
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(e => 
          e.agentName.toLowerCase().includes(query) ||
          e.id.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    
    filteredTransactions: (state) => {
      let filtered = [...state.walletTransactions];
      
      // Company filtering for super admin
      if (state.isSuperAdmin && state.companyFilter !== 'all') {
        const companyName = state.companies.find(c => c.id === state.companyFilter)?.name;
        filtered = filtered.filter(t => t.company === companyName);
      }
      
      // For company admin, only show their company's transactions
      if (state.isCompanyAdmin && state.currentCompany) {
        filtered = filtered.filter(t => t.company === state.currentCompany);
      }
      
      // Period filtering
      if (state.periodFilter === 'current') {
        // For demo, assume current period is anything in the last 30 days
        const oneMonthAgo = new Date();
        oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
        filtered = filtered.filter(t => new Date(t.date) >= oneMonthAgo);
      } else if (state.periodFilter === 'previous') {
        // Previous period is 30-60 days ago
        const oneMonthAgo = new Date();
        const twoMonthsAgo = new Date();
        oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
        twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          return date >= twoMonthsAgo && date < oneMonthAgo;
        });
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(t => 
          t.id.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    
    filteredSchedules: (state) => {
      let filtered = [...state.paymentSchedules];
      
      // Company filtering for super admin
      if (state.isSuperAdmin && state.companyFilter !== 'all') {
        const companyName = state.companies.find(c => c.id === state.companyFilter)?.name;
        filtered = filtered.filter(s => s.company === companyName);
      }
      
      // For company admin, only show their company's schedules
      if (state.isCompanyAdmin && state.currentCompany) {
        filtered = filtered.filter(s => s.company === state.currentCompany);
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(s => 
          s.id.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    
    filteredWallets: (state) => {
      let filtered = [...state.wallets];
      
      // Company filtering for super admin
      if (state.isSuperAdmin && state.companyFilter !== 'all') {
        const companyName = state.companies.find(c => c.id === state.companyFilter)?.name;
        filtered = filtered.filter(w => w.company === companyName);
      }
      
      // For company admin, only show their company's wallets
      if (state.isCompanyAdmin && state.currentCompany) {
        filtered = filtered.filter(w => w.company === state.currentCompany);
      }
      
      // Search filtering
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(w => 
          w.id.toLowerCase().includes(query) ||
          w.name.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    
    getAuditLogsForWallet: (state) => (walletId) => {
      return state.auditLogs.filter(log => log.walletId === walletId);
    }
  },
  
  mutations: {
    setWalletBalance(state, balance) {
      state.walletBalance = balance;
    },
    
    setNextPaymentDate(state, date) {
      state.nextPaymentDate = date;
    },
    
    setPaymentSchedule(state, schedule) {
      state.paymentSchedule = schedule;
    },
    
    setTotalAgentEarnings(state, earnings) {
      state.totalAgentEarnings = earnings;
    },
    
    setAgentEarnings(state, earnings) {
      state.agentEarnings = earnings;
    },
    
    setWalletTransactions(state, transactions) {
      state.walletTransactions = transactions;
    },
    
    setPaymentSchedules(state, schedules) {
      state.paymentSchedules = schedules;
    },
    
    setWallets(state, wallets) {
      state.wallets = wallets;
    },
    
    setAuditLogs(state, logs) {
      state.auditLogs = logs;
    },
    
    addAuditLog(state, log) {
      state.auditLogs.unshift(log);
    },
    
    setLoading(state, isLoading) {
      state.loading = isLoading;
    },
    
    setError(state, error) {
      state.error = error;
    },
    
    setCurrentTab(state, tab) {
      state.currentTab = tab;
    },
    
    setCompanyFilter(state, company) {
      state.companyFilter = company;
    },
    
    setPeriodFilter(state, period) {
      state.periodFilter = period;
    },
    
    setSearchQuery(state, query) {
      state.searchQuery = query;
    },
    
    setUserRole(state, { isSuperAdmin, isCompanyAdmin, currentCompany }) {
      state.isSuperAdmin = isSuperAdmin;
      state.isCompanyAdmin = isCompanyAdmin;
      state.currentCompany = currentCompany;
    },
    
    addWalletTransaction(state, transaction) {
      state.walletTransactions.unshift(transaction);
    },
    
    updateWallet(state, updatedWallet) {
      const index = state.wallets.findIndex(w => w.id === updatedWallet.id);
      if (index !== -1) {
        state.wallets.splice(index, 1, updatedWallet);
      }
    }
  },
  
  actions: {
    async fetchWalletData({ commit, state }) {
      commit('setLoading', true);
      try {
        // In a real app, you would make API calls to fetch this data
        // const response = await axios.get(`${apiBaseUrl}/wallet/data`);
        
        // For now, use mock data
        const mockAgentEarnings = [
          { id: 'E001', agentName: 'John Doe', company: 'Bank A', amount: 5000, date: '2023-10-01', status: 'paid' },
          { id: 'E002', agentName: 'Jane Smith', company: 'Bank B', amount: 3500, date: '2023-10-02', status: 'pending' },
          { id: 'E003', agentName: 'Bob Johnson', company: 'Bank C', amount: 4200, date: '2023-09-28', status: 'paid' },
          { id: 'E004', agentName: 'Alice Brown', company: 'Bank A', amount: 2800, date: '2023-09-25', status: 'paid' },
          { id: 'E005', agentName: 'Charlie Wilson', company: 'Bank B', amount: 3900, date: '2023-10-05', status: 'pending' }
        ];
        
        const mockTransactions = [
          { id: 'T001', company: 'Bank A', amount: 10000, type: 'deposit', description: 'Monthly funding', date: '2023-10-01', status: 'completed' },
          { id: 'T002', company: 'Bank B', amount: 5000, type: 'withdrawal', description: 'Agent payment', date: '2023-09-28', status: 'completed' },
          { id: 'T003', company: 'Bank C', amount: 7500, type: 'deposit', description: 'Additional funding', date: '2023-10-03', status: 'completed' },
          { id: 'T004', company: 'Bank A', amount: 3000, type: 'withdrawal', description: 'System fee', date: '2023-09-20', status: 'completed' },
          { id: 'T005', company: 'Bank B', amount: 8000, type: 'deposit', description: 'Weekly funding', date: '2023-10-07', status: 'pending' }
        ];
        
        const mockSchedules = [
          { id: 'S001', company: 'Bank A', frequency: 'Weekly', day: 'Monday', amount: 10000, description: 'Weekly agent payments', status: 'active' },
          { id: 'S002', company: 'Bank B', frequency: 'Monthly', day: '1st', amount: 25000, description: 'Monthly agent payments', status: 'active' },
          { id: 'S003', company: 'Bank C', frequency: 'Bi-weekly', day: 'Friday', amount: 15000, description: 'Bi-weekly agent payments', status: 'paused' }
        ];
        
        const mockWallets = [
          { id: 'W001', company: 'Bank A', name: 'Main Wallet', balance: 45000, status: 'active' },
          { id: 'W002', company: 'Bank B', name: 'Main Wallet', balance: 32000, status: 'active' },
          { id: 'W003', company: 'Bank C', name: 'Main Wallet', balance: 28000, status: 'active' },
          { id: 'W004', company: 'Bank A', name: 'Reserve Wallet', balance: 15000, status: 'active' },
          { id: 'W005', company: 'Bank B', name: 'Reserve Wallet', balance: 10000, status: 'locked' }
        ];
        
        const mockAuditLogs = [
          { id: 'A001', walletId: 'W001', timestamp: '2023-10-05T10:30:00Z', action: 'Deposit', amount: 10000, performedBy: 'Admin User', details: 'Monthly funding' },
          { id: 'A002', walletId: 'W002', timestamp: '2023-10-03T14:15:00Z', action: 'Withdrawal', amount: 5000, performedBy: 'System', details: 'Agent payment' },
          { id: 'A003', walletId: 'W003', timestamp: '2023-10-01T09:45:00Z', action: 'Deposit', amount: 7500, performedBy: 'Admin User', details: 'Additional funding' },
          { id: 'A004', walletId: 'W001', timestamp: '2023-09-28T16:20:00Z', action: 'Withdrawal', amount: 3000, performedBy: 'System', details: 'System fee' },
          { id: 'A005', walletId: 'W002', timestamp: '2023-09-25T11:10:00Z', action: 'Deposit', amount: 8000, performedBy: 'Admin User', details: 'Weekly funding' }
        ];
        
        commit('setAgentEarnings', mockAgentEarnings);
        commit('setWalletTransactions', mockTransactions);
        commit('setPaymentSchedules', mockSchedules);
        commit('setWallets', mockWallets);
        commit('setAuditLogs', mockAuditLogs);
        
        return true;
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        commit('setError', error.message || 'Failed to load wallet data');
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    async fundWallet({ commit, state }, { walletId, amount, description }) {
      commit('setLoading', true);
      try {
        // In a real app, you would make an API call
        // const response = await axios.post(`${apiBaseUrl}/wallet/${walletId}/fund`, { amount, description });
        
        // For now, simulate the API call
        const wallet = state.wallets.find(w => w.id === walletId);
        if (!wallet) {
          throw new Error('Wallet not found');
        }
        
        // Update wallet balance
        const updatedWallet = {
          ...wallet,
          balance: wallet.balance + amount
        };
        
        // Create transaction record
        const transaction = {
          id: `T${Date.now()}`,
          company: wallet.company,
          amount: amount,
          type: 'deposit',
          description: description || 'Wallet funding',
          date: new Date().toISOString().split('T')[0],
          status: 'completed'
        };
        
        // Create audit log
        const auditLog = {
          id: `A${Date.now()}`,
          walletId: walletId,
          timestamp: new Date().toISOString(),
          action: 'Deposit',
          amount: amount,
          performedBy: 'Current User', // In a real app, get from auth state
          details: description || 'Wallet funding'
        };
        
        // Update state
        commit('updateWallet', updatedWallet);
        commit('addWalletTransaction', transaction);
        commit('addAuditLog', auditLog);
        
        return true;
      } catch (error) {
        console.error('Error funding wallet:', error);
        commit('setError', error.message || 'Failed to fund wallet');
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    async withdrawFromWallet({ commit, state }, { walletId, amount, description }) {
      commit('setLoading', true);
      try {
        // In a real app, you would make an API call
        // const response = await axios.post(`${apiBaseUrl}/wallet/${walletId}/withdraw`, { amount, description });
        
        // For now, simulate the API call
        const wallet = state.wallets.find(w => w.id === walletId);
        if (!wallet) {
          throw new Error('Wallet not found');
        }
        
        if (wallet.balance < amount) {
          throw new Error('Insufficient funds');
        }
        
        // Update wallet balance
        const updatedWallet = {
          ...wallet,
          balance: wallet.balance - amount
        };
        
        // Create transaction record
        const transaction = {
          id: `T${Date.now()}`,
          company: wallet.company,
          amount: amount,
          type: 'withdrawal',
          description: description || 'Wallet withdrawal',
          date: new Date().toISOString().split('T')[0],
          status: 'completed'
        };
        
        // Create audit log
        const auditLog = {
          id: `A${Date.now()}`,
          walletId: walletId,
          timestamp: new Date().toISOString(),
          action: 'Withdrawal',
          amount: amount,
          performedBy: 'Current User', // In a real app, get from auth state
          details: description || 'Wallet withdrawal'
        };
        
        // Update state
        commit('updateWallet', updatedWallet);
        commit('addWalletTransaction', transaction);
        commit('addAuditLog', auditLog);
        
        return true;
      } catch (error) {
        console.error('Error withdrawing from wallet:', error);
        commit('setError', error.message || 'Failed to withdraw from wallet');
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    async updatePaymentSchedule({ commit, state }, { scheduleId, frequency, day, amount, description }) {
      commit('setLoading', true);
      try {
        // In a real app, you would make an API call
        // const response = await axios.put(`${apiBaseUrl}/payment-schedule/${scheduleId}`, { frequency, day, amount, description });
        
        // For now, simulate the API call
        const scheduleIndex = state.paymentSchedules.findIndex(s => s.id === scheduleId);
        if (scheduleIndex === -1) {
          throw new Error('Payment schedule not found');
        }
        
        // Update schedule
        const updatedSchedule = {
          ...state.paymentSchedules[scheduleIndex],
          frequency,
          day,
          amount,
          description
        };
        
        // Create audit log
        const auditLog = {
          id: `A${Date.now()}`,
          walletId: null, // Not specific to a wallet
          timestamp: new Date().toISOString(),
          action: 'Update Schedule',
          amount: amount,
          performedBy: 'Current User', // In a real app, get from auth state
          details: `Updated payment schedule: ${description}`
        };
        
        // Update state
        const updatedSchedules = [...state.paymentSchedules];
        updatedSchedules[scheduleIndex] = updatedSchedule;
        commit('setPaymentSchedules', updatedSchedules);
        commit('addAuditLog', auditLog);
        
        return true;
      } catch (error) {
        console.error('Error updating payment schedule:', error);
        commit('setError', error.message || 'Failed to update payment schedule');
        return false;
      } finally {
        commit('setLoading', false);
      }
    },
    
    async exportFinancialReport({ state }) {
      try {
        // In a real app, you might call an API to generate a report
        // const response = await axios.get(`${apiBaseUrl}/wallet/export-report`, { 
        //   params: {
        //     companyFilter: state.companyFilter,
        //     periodFilter: state.periodFilter,
        //     searchQuery: state.searchQuery,
        //     tab: state.currentTab
        //   },
        //   responseType: 'blob' 
        // });
        
        // For now, just log that we're exporting
        console.log('Exporting financial report with filters:', {
          companyFilter: state.companyFilter,
          periodFilter: state.periodFilter,
          searchQuery: state.searchQuery,
          tab: state.currentTab
        });
        
        // In a real app, you would handle the file download
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', 'financial-report.csv');
        // document.body.appendChild(link);
        // link.click();
        
        return true;
      } catch (error) {
        console.error('Error exporting financial report:', error);
        return false;
      }
    },
    
    updateFilters({ commit }, { companyFilter, periodFilter, searchQuery }) {
      if (companyFilter !== undefined) {
        commit('setCompanyFilter', companyFilter);
      }
      
      if (periodFilter !== undefined) {
        commit('setPeriodFilter', periodFilter);
      }
      
      if (searchQuery !== undefined) {
        commit('setSearchQuery', searchQuery);
      }
    },

    setCurrentTab({ commit }, tab) {
      console.log(tab)
      commit('setCurrentTab', tab);
    },

    getWalletBalance: async (context, payload) => {
      const id = context.rootGetters.userCompany

      let api = globalApiKey;

      // if(!api){
      //   api = 'f6fef6af-0fca-49fe-bec4-89ddb6d24502'
      // }

      try {
        const response = await axios.get(bankApi + `audit/getbalance`, {
          headers: {
            "Api-key": api
          },
          params: {
            bankid: id ? id : null,
          }
        })
        return response.data.response;
      } catch (e) {
        errorHandler.tomcatError(e);
      }
    },

    depositBalance: async (context, payload) => {
      const id = context.rootGetters.userCompany
      try {
        const response = await axios.get(bankApi + `/deposit/bankdeposit`, {
          headers: {
            "Api-key": payload.ApiKey
          },
          params: {
            bankid: id ? id : null,
          }
        })
        return response.data.response;
      } catch (e) {
        errorHandler.tomcatError(e);
      }
    },
  }
}; 